import {FractionalUnits} from '@mintdata/mint-kit/api/models/FractionalUnits';
import {FractionalUnitsType} from '@mintdata/mint-kit/api/models/FractionalUnitsType';
import {IPropertyConverter} from '@mintdata/mint-kit/api/properties/PropertyConverter';
import {ValidationResult} from '@mintdata/mint-kit/api/properties/ValidationResult';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Empty} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {BaseFieldConverter} from '@mintdata/mint-kit/impl/properties/converters/BaseFieldConverter';

export class SimpleFractionalUnitsConverter extends BaseFieldConverter<FractionalUnits>
{
}

export class FractionalUnitsStringConverter implements IPropertyConverter<AString>
{
    public validate(unitsString:string, isRequired:boolean = true):ValidationResult
    {
        unitsString = (unitsString || '').toLowerCase().trim();

        if( !isRequired && unitsString.length === 0 )
            return ValidationResult.valid();

        const units:AString | AError = this.stringToValue(unitsString, isRequired);

        if( Type.isInstanceOf(units, Type.Error) )
        {
            const error:AError = units as AError;
            return ValidationResult.error(error.toString());
        }

        return !units
            ? ValidationResult.error('Invalid PositiveUnits value')
            : ValidationResult.valid();
    }

    public validateValue(units:AString, isRequired:boolean = true):ValidationResult
    {
        return this.validate(units && units.value, isRequired);
    }

    public valueToString(units:AString, isFocused:boolean = false):string
    {
        if( units === Empty )
            return '';

        const unitsString:string = units != null && units.value != null ? units.value : '';

        const parts:Array<string> = /(^-?[0-9]*\.*[0-9]+)(px|%$)/.exec(unitsString);

        if( !isFocused )
        {
            if( parts && parts.length > 2 && parts[1] &&
                (parts[2] === FractionalUnitsType.pixels().value.value
                    || parts[2] === FractionalUnitsType.percentages().value.value
                    || parts[2] === FractionalUnitsType.fractions().value.value) )
            {
                const numberValue:number = parseInt(parts[1], 10);
                const floatValue:number = parseFloat(parts[1]);

                if( !isNaN(numberValue) && !isNaN(floatValue) && numberValue === floatValue )
                    return unitsString;
                else
                    return floatValue.toFixed(2) + parts[2];
            }

            return unitsString;
        }

        if( parts && parts.length > 2 )
            return parts[1];

        return unitsString === FractionalUnitsType.AUTO.value ? '' : unitsString;
    }

    public stringToValue(unitsString:string, isRequired:boolean = true):AString | AError
    {
        unitsString = (unitsString || '').trim();

        if( !isRequired && unitsString.length === 0 )
            return Empty;

        if( unitsString.length > 0 )
        {
            if( unitsString === FractionalUnitsType.AUTO.value )
                return AString.fromString(unitsString);
            else
            {
                const parts:Array<string> = /(^[0-9]*\.*[0-9]+)(\D*$)/.exec(unitsString);

                if( parts && parts.length > 2 && parts[1] &&
                    (parts[2] == '' || parts[2] === FractionalUnitsType.pixels().value.value
                        || parts[2] === FractionalUnitsType.percentages().value.value
                        || parts[2] === FractionalUnitsType.fractions().value.value) )
                    return AString.fromString(
                        parts[1] + (parts[2] != '' ? parts[2] : FractionalUnitsType.pixels().value.value)
                    );
            }
        }
        else
            return AString.fromString(FractionalUnitsType.AUTO.value);

        return AError.fromString('Invalid FractionalUnits value: ' + unitsString);
    }
}

export class FractionalUnitsConverter extends BaseFieldConverter<FractionalUnits>
{
    public validate(unitsString:string, isRequired:boolean = true):ValidationResult
    {
        unitsString = (unitsString || '').toLowerCase().trim();

        if( !isRequired && unitsString.length === 0 )
            return ValidationResult.valid();

        const units:FractionalUnits | AError = this.stringToValue(unitsString, isRequired);

        if( Type.isInstanceOf(units, Type.Error) )
        {
            const error:AError = units as AError;
            return ValidationResult.error(error.toString());
        }

        return !units
            ? ValidationResult.error('Invalid PositiveUnits value')
            : ValidationResult.valid();
    }

    public validateValue(units:FractionalUnits, isRequired:boolean = true):ValidationResult
    {
        return this.validate(units && units.unitsString && units.unitsString.value, isRequired);
    }

    public valueToString(units:FractionalUnits, isFocused:boolean = false):string
    {
        if( units === Empty )
            return '';

        const unitsString:string = units != null && units.unitsString && units.unitsString.value != null
            ? units.unitsString.value
            : '';

        if( !isFocused ) return unitsString;

        const parts:Array<string> = /(^[0-9]*\.*[0-9]+)(px$)/.exec(unitsString);

        if( parts && parts.length > 2 )
            return parts[1];

        return unitsString === FractionalUnitsType.AUTO.value ? '' : unitsString;
    }

    public stringToValue(unitsString:string, isRequired:boolean = true):FractionalUnits | AError
    {
        unitsString = (unitsString || '').trim();

        if( !isRequired && unitsString.length === 0 )
            return Empty;

        if( unitsString.length > 0 )
        {
            if( unitsString === FractionalUnitsType.AUTO.value )
                return FractionalUnits.fromFractionalUnitsString(AString.fromString(unitsString));
            else
            {
                const parts:Array<string> = /(^[0-9]*\.*[0-9]+)(\D*$)/.exec(unitsString);

                if( parts && parts.length > 2 && parts[1] &&
                    (parts[2] == '' || parts[2] === FractionalUnitsType.pixels().value.value
                        || parts[2] === FractionalUnitsType.percentages().value.value
                        || parts[2] === FractionalUnitsType.fractions().value.value) )
                    return FractionalUnits.fromFractionalUnitsString(
                        AString.fromString(
                            parts[1] + (parts[2] != '' ? parts[2] : FractionalUnitsType.pixels().value.value)
                        )
                    );
            }
        }
        else
            return FractionalUnits.fromFractionalUnitsString(AString.fromString(FractionalUnitsType.AUTO.value));

        return AError.fromString('Invalid PositiveUnits value: ' + unitsString);
    }

    public static readonly VALUE_FIELD = 'value';
    public static readonly UNITS_TYPE_FIELD = 'unitsType';
    public static readonly UNITS_STRING_FIELD = 'unitsString';
}
